import { ROLES } from "@/config/roles.js";

/**
 * Definición del menú lateral.
 * allowedRoles controla qué rol ve cada ítem.
 *
 * `name` es SIEMPRE una clave de i18n (ver src/i18n.js, namespace `nav`).
 * Las cadenas se guardan en minúscula: el aside aplica `text-capitalize-first`,
 * así que la mayúscula inicial la pone el CSS y el menú queda uniforme.
 *
 * Una misma entidad de BD se muestra con distinto nombre según el perfil:
 *   Company → "usuario" (usuario) / "emisor" (contador)
 *   Contact → "cliente" (usuario) / "receptor" (contador)
 */
const menuItems = [
  // ── ADMIN + SUPER_ADMIN ────────────────────────────────────────────────────
  {
    key: 3,
    name: "nav.folios",
    icon: "bi-ticket-fill",
    path: "",
    allowedRoles: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
    subMenu: [
      {
        key: 3.1,
        name: "nav.foliosPurchased",
        icon: "bi-ticket-detailed",
        path: "/folios",
        allowedRoles: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
      },
    ],
  },
  {
    key: 7,
    name: "nav.bills",
    icon: "bi-receipt",
    path: "",
    allowedRoles: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
    subMenu: [
      {
        key: 7.1,
        name: "nav.billsDetail",
        icon: "bi-receipt-cutoff",
        path: "/billsAdmin",
        allowedRoles: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
      },
    ],
  },
  {
    key: 1,
    name: "nav.internalUsers",
    icon: "bi-person",
    path: "",
    allowedRoles: [ROLES.SUPER_ADMIN],
    subMenu: [
      {
        key: 1.1,
        name: "nav.internalUsers",
        icon: "bi-person",
        path: "/users-internal",
        allowedRoles: [ROLES.SUPER_ADMIN],
      },
    ],
  },
  {
    key: 2,
    name: "nav.issuer",
    icon: "bi-building",
    path: "",
    allowedRoles: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
    subMenu: [
      {
        key: 2.1,
        name: "nav.issuerData",
        icon: "",
        path: "/companies",
        allowedRoles: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
      },
      // "Usuarios" (/client-users) salió del menú (Bloque T, tarjeta #1). La
      // ruta y /client-users/:userId/show siguen vivas: la columna "Dueño" de
      // emisores y receptores enlaza ahí.
    ],
  },
  {
    key: 8,
    name: "nav.receiver",
    icon: "bi-person-lines-fill",
    path: "",
    allowedRoles: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
    subMenu: [
      {
        key: 8.1,
        name: "nav.receiverData",
        icon: "bi-journal-album",
        path: "/contacts-admin",
        allowedRoles: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
      },
    ],
  },

  // ── SOLO SUPER_ADMIN ───────────────────────────────────────────────────────
  {
    key: 20,
    name: "nav.settings",
    icon: "bi-gear",
    path: "",
    allowedRoles: [ROLES.SUPER_ADMIN],
    subMenu: [
      {
        key: 20.1,
        name: "nav.notifications",
        icon: "bi-bell",
        path: "/settings/notifications",
        allowedRoles: [ROLES.SUPER_ADMIN],
      },
      {
        key: 20.2,
        name: "nav.sentEmails",
        icon: "bi-envelope-paper",
        path: "/settings/emails",
        allowedRoles: [ROLES.SUPER_ADMIN],
      },
      {
        key: 20.25,
        name: "nav.packagesAdmin",
        icon: "bi-box-seam",
        path: "/settings/packages",
        allowedRoles: [ROLES.SUPER_ADMIN],
      },
      {
        key: 20.3,
        name: "nav.impersonate",
        icon: "bi-person-check",
        path: "/impersonate",
        allowedRoles: [ROLES.SUPER_ADMIN],
      },
      // El único de este grupo que también ve el contador: el aside enseña el
      // grupo en cuanto le queda un submenú, aunque el título sea de master.
      {
        key: 20.4,
        name: "nav.mcp",
        icon: "bi-plug",
        path: "/settings/mcp",
        allowedRoles: [ROLES.ADMIN, ROLES.SUPER_ADMIN],
      },
    ],
  },

  // ── USER ───────────────────────────────────────────────────────────────────
  {
    key: 9,
    name: "nav.account",
    icon: "bi-building",
    path: "",
    allowedRoles: [ROLES.USER],
    subMenu: [
      {
        key: 9.1,
        name: "nav.accountData",
        icon: "bi-buildings",
        path: "/my-companies",
        allowedRoles: [ROLES.USER],
      },
    ],
  },
  {
    key: 4,
    name: "nav.clients",
    icon: "bi-person-lines-fill",
    path: "",
    allowedRoles: [ROLES.USER],
    subMenu: [
      {
        key: 4.1,
        name: "nav.clientsList",
        icon: "bi-journal-album",
        path: "/contacts",
        allowedRoles: [ROLES.USER],
      },
    ],
  },
  {
    key: 6,
    name: "nav.requestBill",
    icon: "bi-receipt",
    path: "",
    allowedRoles: [ROLES.USER],
    subMenu: [
      {
        key: 6.1,
        name: "nav.requestedBills",
        icon: "bi-receipt-cutoff",
        path: "/bills",
        allowedRoles: [ROLES.USER],
      },
    ],
  },
  {
    key: 100,
    name: "nav.packages",
    icon: "bi-boxes",
    path: "",
    allowedRoles: [ROLES.USER],
    subMenu: [
      {
        key: 100.1,
        name: "nav.packagesDetail",
        icon: "bi-box-seam-fill",
        path: "/request_tickets",
        allowedRoles: [ROLES.USER],
      },
      {
        key: 100.2,
        name: "nav.packagesBuy",
        icon: "bi-cart-plus",
        path: "/buy_package",
        allowedRoles: [ROLES.USER],
      },
    ],
  },

  // ── COMPARTIDO ─────────────────────────────────────────────────────────────
  {
    key: 5,
    name: "nav.profile",
    icon: "bi-person-square",
    path: "",
    allowedRoles: [ROLES.USER, ROLES.ADMIN, ROLES.SUPER_ADMIN],
    subMenu: [
      {
        key: 5.1,
        name: "your profile",
        icon: "bi-person-bounding-box",
        path: "/my_profile",
        allowedRoles: [ROLES.USER, ROLES.ADMIN, ROLES.SUPER_ADMIN],
      },
    ],
  },
];

export default menuItems;
